import React, { useEffect, useRef, useState } from "react";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import dataService from "@/app/services/dataService";
import { Lookup } from "@/app/models/common/types";

function debounceQuickSearchPromise(fn: (...args: any[]) => Promise<any>, delay: number) {
  let timer: any;
  return (...args: any[]) => {
    return new Promise((resolve) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => resolve(fn(...args)), delay);
    });
  };
}

export const BranchAutocomplete: React.FC<{
  onChange?: (value: Lookup | null) => void;
  branchInitialValue?: Lookup | null;
}> = ({ onChange, branchInitialValue }) => {
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState<Lookup[]>([]);
  const [value, setValue] = useState<Lookup | null>(branchInitialValue ?? null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const pageSize = 15;

  const debouncedFetchForInput = useRef(
    debounceQuickSearchPromise(async (search: string) => {
      setLoading(true);
      try {
        const response = await dataService.getBranchLookUp(search, 1, pageSize);
        const data = response.data;

        const mapped = Array.isArray(data)
          ? data.map((item: any) => ({
            id: item.id,
            displayName: item.displayName,
          }))
          : [];

        setHasMore(mapped.length === pageSize);
        setOptions(mapped);
        setPage(1);
      } catch (error) {
        console.error("Error fetching warehouses:", error);
        setOptions([]);
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    }, 500)
  ).current;

  const fetchPage = async (search: string, pageNumber: number, append: boolean = false) => {
    setLoading(true);
    try {
      const response = await dataService.getBranchLookUp(search, pageNumber, pageSize);
      const data = response.data;

      const mapped = Array.isArray(data)
        ? data.map((item: any) => ({
            id: item.id,
            displayName: item.displayName,
          }))
        : [];

      setHasMore(mapped.length === pageSize);
      setOptions((prev) => (append ? [...prev, ...mapped] : mapped));
    } catch (error) {
      setOptions([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (branchInitialValue) {
      setValue(branchInitialValue);

      setOptions((prev) => {
        const exists = prev.some((opt) => opt.id === branchInitialValue.id);
        return exists ? prev : [branchInitialValue, ...prev];
      });

      onChange?.(branchInitialValue);
    }
  }, [branchInitialValue]);

  useEffect(() => {
    if (!inputValue.trim()) {
      setOptions([]);
      setPage(1);
      setHasMore(true);
      return;
    }

    debouncedFetchForInput(inputValue);
  }, [inputValue]);

  const handleOpen = () => {
    fetchPage(inputValue.trim(), 1, false);
    setPage(1);
  };

  const handleScroll = (event: React.SyntheticEvent) => {
    const listboxNode = event.currentTarget;
    const { scrollTop, scrollHeight, clientHeight } = listboxNode;

    const nearBottom = scrollHeight - scrollTop <= clientHeight + 50;
    if (nearBottom && !loading && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchPage(inputValue, nextPage, true);
    }
  };

  return (
    <Autocomplete
      fullWidth
      loading={loading}
      options={options}
      value={value}
      onOpen={handleOpen}
      onChange={(_, newValue) => {
        setValue(newValue);
        onChange?.(newValue);
      }}
      onInputChange={(_, newInputValue, reason) => {
        if (reason === "input") {
          setInputValue(newInputValue);
        }
      }}
      getOptionLabel={(option) => option.displayName}
      isOptionEqualToValue={(option, value) => option.id === value?.id}
      ListboxProps={{
        onScroll: handleScroll,
        style: { maxHeight: 300, overflow: "auto" },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          fullWidth
          size="small"
          className="form-input"
          InputLabelProps={{ shrink: false }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};